import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, catchError, map, of, tap, throwError } from 'rxjs';
import { BackendResponse, UserForm } from '../../types/interfaces';
import { ConfigService } from '../config';
import { Profile, ShopCustomerDto } from '../../../shared/models/profile.model';
import { deserializeResponse, replaceUrlParams } from '../utils/helpers';
import { NewUser, User } from '../../../shared/models/user.model';
import { TranslateService } from '@ngx-translate/core';
import { SnackbarService } from '../utils/snackbar.service';
import { ResponseStatus } from '../../types/enums';
import { TestCustomerService } from '../test-customer/test-customer.service';
import { AuthenticationService } from '../authentication';

@Injectable({
  providedIn: 'root',
})
export class ProfileService {
  constructor(
    private http: HttpClient,
    private configService: ConfigService,
    private snackbarService: SnackbarService,
    private translateService: TranslateService,
    private testCustomerService: TestCustomerService
  ) {}

  getProfile(): Observable<BackendResponse<Profile>> {
    if (
      this.testCustomerService.isLoggedInAsTestCustomer() &&
      this.testCustomerService.hasCustomerProfile()
    ) {
      return of({
        status: ResponseStatus.Success,
        message: 'Test customer profile loaded successfully',
        data: this.testCustomerService.loadCustomerProfile(),
      });
    }
    const url = this.configService.endpoints.profile.profile;
    return this.http.get<BackendResponse<Profile>>(url).pipe(
      catchError(error => {
        if (error.isHandled) return;
        this.snackbarService.error(
          this.translateService.instant('profile.error.inProfileRetrieving')
        );
        return throwError(() => error);
      }),
      map((arg: any) => deserializeResponse<Profile>(arg, Profile)),
      tap(customerProfile => {
        if (this.testCustomerService.isLoggedInAsTestCustomer()) {
          this.testCustomerService.saveCustomerProfile(customerProfile.data);
        }
      })
    );
  }
  updateProfile(profile: Profile): Observable<BackendResponse> {
    if (this.testCustomerService.isLoggedInAsTestCustomer()) {
      this.testCustomerService.saveCustomerProfile(profile);
      return of({
        status: ResponseStatus.Success,
        message: 'Test customer profile saved successfully',
      });
    }
    const url = this.configService.endpoints.profile.profile;
    return this.http
      .put<BackendResponse<ShopCustomerDto>>(url, new ShopCustomerDto(profile))
      .pipe(
        catchError(error => {
          if (error.isHandled) return;
          this.snackbarService.error(
            this.translateService.instant('profile.error.inProfileSaving')
          );
          return throwError(() => error);
        })
      );
  }

  getUserProfile(userId: string): Observable<BackendResponse<User>> {
    if (this.testCustomerService.isLoggedInAsTestCustomer()) {
      const testUser = this.testCustomerService.getUserById(userId);
      if (testUser) {
        return of({
          status: ResponseStatus.Success,
          message: 'Test user profile loaded successfully',
          data: testUser,
        });
      }
    }
    const url = this.configService.endpoints.profile.user;
    return this.http
      .get<BackendResponse<User>>(url, { params: { userId } })
      .pipe(
        map((arg: any) => deserializeResponse<User>(arg, User)),
        catchError(error => {
          if (error.isHandled) return;
          this.snackbarService.error(
            this.translateService.instant('users.error.inUserRetrieving')
          );
          return throwError(() => error);
        })
      );
  }

  updateUserProfile(user: User): Observable<BackendResponse> {
    if (this.testCustomerService.isLoggedInAsTestCustomer()) {
      this.testCustomerService.updateUser(user);
      return of({
        status: ResponseStatus.Success,
        message: 'User updated successfully for test customer',
      });
    }
    const url = this.configService.endpoints.profile.user;
    return this.http.put<BackendResponse<User>>(url, user).pipe(
      catchError(error => {
        if (error.isHandled) return;
        this.snackbarService.error(
          this.translateService.instant('users.error.inUserUpdating')
        );
        return throwError(() => error);
      })
    );
  }

  deleteUserProfile(userId: string): Observable<BackendResponse> {
    if (this.testCustomerService.isLoggedInAsTestCustomer()) {
      this.testCustomerService.deleteUser(userId);
      return of({
        status: ResponseStatus.Success,
        message: 'User deleted successfully for test customer',
      });
    }
    const url = this.configService.endpoints.profile.user;
    return this.http.delete<BackendResponse>(url, { params: { userId } }).pipe(
      catchError(error => {
        if (error.isHandled) return;
        this.snackbarService.error(
          this.translateService.instant('users.error.inUserRemoving')
        );
        return throwError(() => error);
      })
    );
  }

  createUserProfile(user: UserForm): Observable<BackendResponse<string>> {
    if (this.testCustomerService.isLoggedInAsTestCustomer()) {
      const userId = this.testCustomerService.saveUser(user);
      return of({
        status: ResponseStatus.Success,
        message: 'User created successfully for test customer',
        data: userId,
      });
    }
    const url = this.configService.endpoints.profile.user;
    return this.http.post<BackendResponse<string>>(url, user).pipe(
      map(res => res),
      catchError(error => {
        if (error.isHandled) return;
        return throwError(() => error);
      })
    );
  }

  generateAPIToken(): Observable<BackendResponse<{ token: string }>> {
    const url = this.configService.endpoints.profile.token;
    return this.http.put<BackendResponse<{ token: string }>>(url, {}).pipe(
      map(res => res),
      catchError(error => {
        if (error.isHandled) return;
        return throwError(() => error);
      })
    );
  }
}
